'use client'

import Image from 'next/image'

import { cn } from '@/lib/utils'
import { ProgressiveBlur } from '@/components/ui/progressive-blur'

import { EASE, Eyebrow, QuietLink, useReveal } from './shared'

/* The customer quote stands on its own — no photograph beside it. campus4.jpg
   is the only UDC asset with the resolution for a full-width band, but it
   shows a Euclideum session at a Google Cloud education event, not D Y Patil.
   Running it under the D Y Patil director's words would have implied it was
   their campus, so the band is a separate page element (UdcFieldBand, placed
   before the closing section) with alt text that says what it actually is.

   Everything else on this page is drawn rather than photographed. */

export function UdcProof() {
  const block = useReveal<HTMLDivElement>(0.15)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <div ref={block.ref} className="max-w-4xl">
          <Eyebrow>Proof</Eyebrow>

          <blockquote
            className="mt-6"
            style={{
              opacity: block.shown ? 1 : 0,
              transform: block.shown ? 'none' : 'translateY(12px)',
              transition: `opacity 700ms ${EASE} 60ms, transform 700ms ${EASE} 60ms`,
            }}
          >
            <p className="font-heading text-2xl font-normal leading-[1.3] tracking-tight text-foreground sm:text-3xl lg:text-[40px] lg:leading-[1.25]">
              “The transition felt effortless. What changed was not just our
              system. It was the way our academic ecosystem started functioning
              with clarity and confidence.”
            </p>

            <footer className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span
                aria-hidden
                className="h-px w-8 bg-emerald-600/60 dark:bg-primary-active/50"
              />
              <cite className="text-sm not-italic font-medium text-foreground">
                Director, D Y Patil College
              </cite>
              <span className="text-sm text-muted-foreground">
                Engineering &amp; Technology
              </span>
            </footer>
          </blockquote>

          <div
            className="mt-8"
            style={{
              opacity: block.shown ? 1 : 0,
              transition: `opacity 700ms ${EASE} 260ms`,
            }}
          >
            <QuietLink href="/customer-stories">Read the full story</QuietLink>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Full-bleed image band used as the transition into the closing section. The
 * mask carries the tail and ProgressiveBlur dissolves the image into the page,
 * so there is no seam and no hard edge — the photo is treated as material, not
 * framed and tinted like a stock-photo card.
 */
export function UdcFieldBand() {
  return (
    <div
      className={cn(
        'relative w-full overflow-hidden',
        'mt-16 h-[clamp(200px,34vw,420px)] sm:mt-20 md:mt-28'
      )}
      style={{
        // Fades in as well as out: a hard top edge reads as a pasted-in
        // rectangle, which is exactly the stock-photo-card look this page
        // avoids everywhere else.
        WebkitMaskImage:
          'linear-gradient(to bottom, transparent 0%, black 16%, black 56%, rgba(0,0,0,0.5) 82%, transparent 100%)',
        maskImage:
          'linear-gradient(to bottom, transparent 0%, black 16%, black 56%, rgba(0,0,0,0.5) 82%, transparent 100%)',
      }}
    >
      <Image
        src="/products/campus4.webp"
        alt="A Euclideum Unified Digital Campus session presented at a Google Cloud education event"
        fill
        quality={90}
        sizes="100vw"
        className="object-cover object-center"
      />
      <ProgressiveBlur direction="bottom" start={58} />
    </div>
  )
}
